import React, { useState } from "react";
import { ArrowLeft, Send } from "lucide-react";
import { API } from "../api";
import { ActionButton, Confirmation, ErrorNote } from "./Feedback";

/** Propose an organisation that does not exist yet, for a NEED administrator to approve. */
export default function CreateOrgForm({ onDone, onCancel }) {
  const [name, setName] = useState("");
  const [domains, setDomains] = useState("");
  const [justification, setJustification] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(null);
  const [error, setError] = useState("");

  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      const res = await API.post("/api/orgs/org-requests", {
        name: name.trim(),
        domains: domains.split(",").map((d) => d.trim()).filter(Boolean),
        justification,
      });
      setSent({ name: name.trim(), notified: res.data?.notified });
      setTimeout(() => onDone(), 1400);
    } catch (err) {
      setError(err.response?.data?.detail || "We could not submit that proposal.");
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <Confirmation
          title={`${sent.name} submitted for approval`}
          detail={
            sent.notified
              ? `${sent.notified} NEED ${
                  sent.notified === 1 ? "administrator has" : "administrators have"
                } been notified by email. You become its first administrator once approved.`
              : "A NEED administrator will review it. You become its first administrator once approved."
          }
        />
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
        <h2 className="text-sm font-black tracking-tight text-slate-900">
          Propose an organisation
        </h2>
        <button
          type="button"
          onClick={onCancel}
          className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs font-bold text-slate-500 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-600"
        >
          <ArrowLeft size={13} /> Back
        </button>
      </div>

      <div className="p-6">
        <p className="mb-6 text-sm leading-relaxed text-slate-600">
          A NEED administrator reviews new organisations. Once approved you become its
          first administrator and can invite your colleagues.
        </p>

        <Field
          id="org-name"
          label="Organisation name"
          required
          hint="Shown throughout the catalog — use the institution's usual name."
        >
          <input
            id="org-name"
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="fortiss"
            className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm focus:border-green-500 focus:outline-none focus:ring-1 focus:ring-green-500"
          />
        </Field>

        <Field
          id="org-domains"
          label="Email domains"
          hint="Comma separated. Used to recognise colleagues later."
        >
          <input
            id="org-domains"
            value={domains}
            onChange={(e) => setDomains(e.target.value)}
            placeholder="fortiss.org, need.energy"
            className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm focus:border-green-500 focus:outline-none focus:ring-1 focus:ring-green-500"
          />
        </Field>

        <Field id="org-why" label="Why this organisation should exist">
          <textarea
            id="org-why"
            rows={3}
            value={justification}
            onChange={(e) => setJustification(e.target.value)}
            placeholder="What data you intend to publish, and on whose behalf."
            className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm focus:border-green-500 focus:outline-none focus:ring-1 focus:ring-green-500"
          />
        </Field>

        {error && <ErrorNote>{error}</ErrorNote>}

        <div className="mt-6 flex justify-end">
          <ActionButton
            icon={Send}
            busy={busy}
            busyLabel="Submitting…"
            disabled={!name.trim()}
            onClick={submit}
          >
            Submit for approval
          </ActionButton>
        </div>
      </div>
    </div>
  );
}

function Field({ id, label, hint, required, children }) {
  return (
    <div className="mb-5">
      <label
        htmlFor={id}
        className="mb-2 block text-[11px] font-black uppercase tracking-[0.18em] text-slate-400"
      >
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {children}
      {hint && <p className="mt-1.5 text-xs leading-relaxed text-slate-500">{hint}</p>}
    </div>
  );
}
